<template>
  <footer class="app-footer">
    <div class="container footer-main" :class="{ 'has-channels': visibleChannels.length }">
      <div class="footer-brand">
        <router-link to="/" class="brand-name">
          <img v-if="showLogoImage" :src="site.logoUrl" alt="" class="brand-logo-img">
          <span v-else class="brand-icon">{{ site.logoEmoji }}</span>
          {{ site.name }}
        </router-link>
        <p class="brand-desc">{{ site.slogan }}</p>
      </div>

      <div class="footer-nav">
        <h4>导航</h4>
        <div class="nav-rows">
          <div class="nav-row">
            <router-link
              v-for="item in mainNav"
              :key="'m-' + item.label"
              :to="getNavTo(item)"
              class="nav-link"
            >
              {{ item.label }}
            </router-link>
          </div>
          <div v-if="categoryNav.length" class="nav-row">
            <router-link
              v-for="item in categoryNav"
              :key="'c-' + item.label"
              :to="getNavTo(item)"
              class="nav-link"
            >
              {{ item.label }}
            </router-link>
          </div>
        </div>
      </div>

      <div class="footer-about">
        <h4>关于</h4>
        <div class="about-compact">
          <div class="about-actions">
            <router-link to="/copyright" class="about-chip">
              <i class="el-icon-document"></i>
              版权声明
            </router-link>
            <a :href="feedbackMailto" class="about-chip is-green">
              <i class="el-icon-message"></i>
              反馈建议
            </a>
          </div>
          <p v-if="site.footerTip" class="about-tip">
            <i class="el-icon-info"></i>
            {{ site.footerTip }}
          </p>
        </div>
      </div>

      <div v-if="visibleChannels.length" class="footer-channels">
        <h4>关注我们</h4>
        <p class="channels-hint">悬停或点击二维码可放大扫码</p>
        <div class="channel-list">
          <div
            v-for="item in visibleChannels"
            :key="item.key"
            class="channel-card"
            :class="[item.key, { open: previewKey === item.key }]"
            @mouseenter="onChannelEnter(item.key)"
            @mouseleave="onChannelLeave"
            @click.stop="toggleChannelPreview(item.key)"
          >
            <div class="qr-frame">
              <img :src="item.imageUrl" :alt="item.label + '二维码'" loading="lazy">
              <span class="qr-zoom-badge" aria-hidden="true">
                <i class="el-icon-zoom-in"></i>
              </span>
            </div>
            <div class="channel-meta">
              <strong>{{ item.label }}</strong>
              <span v-if="item.tip">{{ item.tip }}</span>
            </div>

            <transition name="qr-pop">
              <div
                v-show="previewKey === item.key || hoverKey === item.key"
                class="qr-popover"
                :class="item.key"
                role="dialog"
                :aria-label="item.label + '放大二维码'"
                @click.stop
              >
                <div class="qr-popover-inner">
                  <div class="qr-popover-head">
                    <span class="qr-popover-badge" :class="item.key">{{ item.label }}</span>
                    <button
                      type="button"
                      class="qr-popover-close"
                      aria-label="关闭"
                      @click.stop="closeChannelPreview"
                    >
                      <i class="el-icon-close"></i>
                    </button>
                  </div>
                  <div class="qr-popover-frame">
                    <img :src="item.imageUrl" :alt="item.label + '放大二维码'">
                  </div>
                  <p class="qr-popover-tip">
                    {{ item.tip || '打开微信扫一扫' }}
                  </p>
                </div>
                <span class="qr-popover-arrow" aria-hidden="true" />
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <div class="footer-bottom">
      <div class="container bottom-inner">
        <span>&copy; {{ copyrightDisplay }} {{ site.name }}版权所有</span>
        <span class="dot">·</span>
        <span>
          联系邮箱
          <a :href="contactMailto">{{ displayContactEmail }}</a>
        </span>
        <template v-if="filing.icpNumber">
          <span class="dot">·</span>
          <a
            :href="filing.icpUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="icp-link"
            :title="filingHint"
          >
            {{ filing.icpNumber }}
          </a>
        </template>
        <template v-if="filing.policeNumber">
          <span class="dot">·</span>
          <a
            v-if="filing.policeUrl"
            :href="filing.policeUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="icp-link police-link"
          >
            {{ filing.policeNumber }}
          </a>
          <span v-else class="icp-link police-link">{{ filing.policeNumber }}</span>
        </template>
      </div>
    </div>
  </footer>
</template>

<script>
import { siteSettings } from '@/store/siteSettings'
import { formatCopyrightYears } from '@/config/site'
import { resolveDomainFiling } from '@/utils/domainFiling'

const CHANNEL_ORDER = ['miniprogram', 'wechat']

export default {
  name: 'AppFooter',
  data() {
    return {
      hoverKey: '',
      previewKey: '',
      leaveTimer: null
    }
  },
  computed: {
    site() {
      return siteSettings.config
    },
    copyrightDisplay() {
      return this.site.copyrightYearDisplay
        || formatCopyrightYears(this.site.copyrightYear, this.site.copyrightYearEnd)
    },
    filing() {
      return resolveDomainFiling(this.site)
    },
    filingHint() {
      if (this.filing.source === 'domain') {
        const label = this.filing.label || this.filing.host
        return label ? `当前域名备案：${label}` : '当前域名备案'
      }
      return ''
    },
    displayContactEmail() {
      return 'l77895@qq.com'
    },
    contactMailto() {
      return `mailto:${this.displayContactEmail}`
    },
    navItems() {
      return siteSettings.navItems
    },
    showLogoImage() {
      return this.site.logoType === 'image' && this.site.logoUrl
    },
    mainNav() {
      const items = this.navItems
      if (items.length <= 3) return items
      return [items[0], items[1], items[items.length - 1]]
    },
    categoryNav() {
      const items = this.navItems
      if (items.length <= 3) return []
      return items.slice(2, items.length - 1)
    },
    feedbackMailto() {
      return `mailto:${this.site.feedbackEmail || this.site.contactEmail || this.displayContactEmail}`
    },
    visibleChannels() {
      const channels = this.site.footerChannels || {}
      return CHANNEL_ORDER
        .map((key) => {
          const item = channels[key]
          if (!item || !item.enabled || !item.imageUrl) return null
          return {
            key,
            label: item.label || (key === 'miniprogram' ? '小程序' : '公众号'),
            tip: item.tip || '',
            imageUrl: item.imageUrl
          }
        })
        .filter(Boolean)
    }
  },
  mounted() {
    document.addEventListener('click', this.onDocClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClick)
    if (this.leaveTimer) clearTimeout(this.leaveTimer)
  },
  methods: {
    onChannelEnter(key) {
      if (this.leaveTimer) {
        clearTimeout(this.leaveTimer)
        this.leaveTimer = null
      }
      // 桌面悬停预览；触控设备主要靠点击
      if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
        this.hoverKey = key
      }
    },
    onChannelLeave() {
      if (this.leaveTimer) clearTimeout(this.leaveTimer)
      this.leaveTimer = setTimeout(() => {
        this.hoverKey = ''
      }, 120)
    },
    toggleChannelPreview(key) {
      this.previewKey = this.previewKey === key ? '' : key
      this.hoverKey = ''
    },
    closeChannelPreview() {
      this.previewKey = ''
      this.hoverKey = ''
    },
    onDocClick() {
      if (this.previewKey) this.previewKey = ''
    },
    getNavTo(item) {
      if (item.query) {
        return { path: item.path, query: item.query }
      }
      return item.path
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.app-footer {
  margin-top: 48px;
  background:
    radial-gradient(ellipse 80% 60% at 10% 0%, rgba(37, 99, 235, 0.06), transparent 55%),
    radial-gradient(ellipse 70% 50% at 90% 10%, rgba(14, 165, 233, 0.05), transparent 50%),
    linear-gradient(180deg, $bg-gray 0%, #f1f5f9 100%);
  border-top: 1px solid $border-color;
  overflow: visible;
}

.footer-main {
  display: grid;
  grid-template-columns: 1.15fr 1.25fr 1.25fr;
  gap: 28px 32px;
  padding: 28px 24px 22px;
  align-items: start;
  overflow: visible;

  &.has-channels {
    grid-template-columns: 1.1fr 1.15fr 1.1fr 1.15fr;
  }
}

.brand-name {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 17px;
  font-weight: 700;
  color: $primary-color;
  margin-bottom: 6px;
  transition: color 0.2s;

  &:hover {
    color: $primary-light;
  }
}

.brand-icon {
  font-size: 20px;
  line-height: 1;
}

.brand-logo-img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.brand-desc {
  color: $text-muted;
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
  max-width: 28ch;
}

.footer-nav,
.footer-about,
.footer-channels {
  h4 {
    font-size: 13px;
    font-weight: 700;
    color: $text-primary;
    margin: 0 0 10px;
    letter-spacing: 0.02em;
  }
}

.nav-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.nav-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.nav-link {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  font-size: 13px;
  color: $text-secondary;
  background: $bg-white;
  border: 1px solid $border-color;
  border-radius: 20px;
  transition: all 0.2s;

  &:hover {
    color: $primary-color;
    border-color: $primary-border;
    background: $primary-bg;
    transform: translateY(-1px);
  }
}

.about-compact {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.about-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.about-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 14px;
  font-size: 13px;
  color: $text-secondary;
  background: $bg-white;
  border: 1px solid $border-color;
  border-radius: 20px;
  transition: all 0.2s;

  i {
    font-size: 13px;
    color: $primary-color;
  }

  &:hover {
    color: $primary-color;
    border-color: $primary-border;
    background: $primary-bg;
    transform: translateY(-1px);
    text-decoration: none;
  }

  &.is-green i {
    color: #059669;
  }

  &.is-green:hover {
    background: #ecfdf5;
    border-color: #a7f3d0;
    color: #059669;
  }
}

.about-tip {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  font-size: 11px;
  line-height: 1.45;
  color: $text-muted;
  margin: 0;

  i {
    flex-shrink: 0;
    margin-top: 1px;
    font-size: 12px;
  }
}

.channel-list {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  overflow: visible;
}

.footer-channels {
  position: relative;
  z-index: 5;
  overflow: visible;
}

.channels-hint {
  margin: -4px 0 12px;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.4;
}

.channel-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 128px;
  padding: 12px 10px 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(226, 232, 240, 0.95);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
  cursor: pointer;
  user-select: none;
  overflow: visible;
  z-index: 1;

  &:hover,
  &.open {
    z-index: 20;
    transform: translateY(-2px);
    box-shadow: 0 14px 32px rgba(37, 99, 235, 0.12);
    border-color: #93c5fd;
  }

  &.miniprogram:hover,
  &.miniprogram.open {
    border-color: #6ee7b7;
    box-shadow: 0 14px 32px rgba(16, 185, 129, 0.14);
  }

  &.wechat:hover,
  &.wechat.open {
    border-color: #7dd3fc;
    box-shadow: 0 14px 32px rgba(14, 165, 233, 0.14);
  }
}

.qr-frame {
  position: relative;
  width: 100px;
  height: 100px;
  padding: 7px;
  border-radius: 14px;
  background:
    linear-gradient(#fff, #fff) padding-box,
    linear-gradient(145deg, #bfdbfe, #e0f2fe, #a7f3d0) border-box;
  border: 1px solid transparent;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    border-radius: 8px;
  }
}

.qr-zoom-badge {
  position: absolute;
  right: 6px;
  bottom: 6px;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: rgba(15, 23, 42, 0.72);
  color: #fff;
  font-size: 12px;
  backdrop-filter: blur(6px);
  opacity: 0.88;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.channel-card:hover .qr-zoom-badge,
.channel-card.open .qr-zoom-badge {
  transform: scale(1.06);
  opacity: 1;
}

.channel-meta {
  text-align: center;

  strong {
    display: block;
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
  }

  span {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    color: #94a3b8;
    line-height: 1.35;
  }
}

.qr-popover {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 14px);
  transform: translateX(-50%);
  z-index: 30;
  width: 236px;
  filter: drop-shadow(0 18px 36px rgba(15, 23, 42, 0.18));
  pointer-events: auto;
}

.qr-popover-inner {
  padding: 14px 14px 12px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), #fff);
  border: 1px solid rgba(226, 232, 240, 0.95);
  backdrop-filter: blur(10px);
}

.qr-popover.miniprogram .qr-popover-inner {
  border-color: rgba(110, 231, 183, 0.65);
}

.qr-popover.wechat .qr-popover-inner {
  border-color: rgba(125, 211, 252, 0.7);
}

.qr-popover-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.qr-popover-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;

  &.miniprogram {
    color: #047857;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  &.wechat {
    color: #0369a1;
    background: #f0f9ff;
    border: 1px solid #bae6fd;
  }
}

.qr-popover-close {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }
}

.qr-popover-frame {
  width: 200px;
  height: 200px;
  margin: 0 auto;
  padding: 10px;
  border-radius: 16px;
  background:
    linear-gradient(#fff, #fff) padding-box,
    linear-gradient(145deg, #93c5fd, #67e8f9, #6ee7b7) border-box;
  border: 1px solid transparent;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    border-radius: 8px;
  }
}

.qr-popover-tip {
  margin: 10px 0 0;
  text-align: center;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.qr-popover-arrow {
  position: absolute;
  left: 50%;
  bottom: -6px;
  width: 14px;
  height: 14px;
  background: #fff;
  border-right: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  transform: translateX(-50%) rotate(45deg);
}

.qr-pop-enter-active,
.qr-pop-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.qr-pop-enter,
.qr-pop-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(6px) scale(0.96);
}

.footer-bottom {
  border-top: 1px solid rgba(226, 232, 240, 0.8);
  background: rgba(255, 255, 255, 0.6);
  padding: 12px 0;
}

.bottom-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px 10px;
  font-size: 12px;
  color: $text-muted;
  line-height: 1.6;
  text-align: center;

  a {
    color: $text-secondary;
    transition: color 0.2s;

    &:hover {
      color: $primary-color;
    }
  }

  .dot {
    color: $border-color;
    user-select: none;
  }

  .icp-link {
    color: $text-muted;
  }
}

@media (max-width: 1024px) {
  .footer-main,
  .footer-main.has-channels {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .app-footer {
    margin-top: 32px;
  }

  .footer-main,
  .footer-main.has-channels {
    grid-template-columns: 1fr;
    gap: 22px;
    padding: 28px 20px 22px;
    text-align: center;
    justify-items: center;
  }

  .footer-brand {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .brand-desc {
    max-width: 36ch;
  }

  .footer-nav,
  .footer-about,
  .footer-channels {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    h4 {
      margin-bottom: 12px;
    }
  }

  .nav-rows {
    align-items: center;
    width: 100%;
  }

  .nav-row,
  .about-actions {
    justify-content: center;
  }

  .about-compact {
    align-items: center;
    width: 100%;
  }

  .about-tip {
    justify-content: center;
    text-align: center;
    max-width: 36ch;
  }

  .channel-list {
    justify-content: center;
  }

  .channel-card {
    width: 140px;
    padding: 14px 12px 13px;
  }

  .qr-frame {
    width: 108px;
    height: 108px;
  }

  .qr-popover {
    width: min(260px, calc(100vw - 32px));
  }

  .qr-popover-frame {
    width: min(220px, calc(100vw - 72px));
    height: min(220px, calc(100vw - 72px));
  }

  .channels-hint {
    text-align: center;
  }

  .bottom-inner {
    flex-direction: column;
    gap: 6px;
    padding: 0 16px;

    .dot {
      display: none;
    }
  }
}
</style>
